/**
 * 週間版 TV デモ用のデータ変換
 * 表示コンポーネントには集計・コメント生成を置かない
 */

import { TV_QR_URL } from '../data/tvDemoDisplay';
import { TV_SPOT_LOCATION_OPTIONS, getTvSpotConfig } from '../data/tvSpotLocation';
import { calibrateHourlyMapForTvSpot } from './tvSpotCalibration';
import { findCalendarDay, resolveHourlyForDate, addDaysIso, parseIsoDate, toIsoDate } from './tvDemoDataTransform';
import { hourlyMapToWeeklyDayStatus, WEEKLY_STATUS_LABELS } from './tvWeeklyStatus';

const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

/** 欠測が多いとみなす閾値（7日中4日以上欠測） */
export const WEEKLY_INSUFFICIENT_MISSING_THRESHOLD = 4;

export const formatWeeklyPeriodLabel = (startIso, endIso) => {
  const a = parseIsoDate(startIso);
  const b = parseIsoDate(endIso);
  const fmt = (dt) => `${dt.getMonth() + 1}月${dt.getDate()}日`;
  return `${fmt(a)}〜${fmt(b)}`;
};

export const formatGeneratedAtLabel = (isoOrDate = new Date()) => {
  const dt = typeof isoOrDate === 'string' ? parseIsoDate(isoOrDate) : isoOrDate;
  const y = dt.getFullYear();
  const m = dt.getMonth() + 1;
  const d = dt.getDate();
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${y}年${m}月${d}日 ${hh}:${mm}`;
};

/** 基準日を含む週の日曜日（日曜始まり・土終わり） */
export const startOfSundayWeek = (iso) => {
  const day = parseIsoDate(iso).getDay(); // 0=日 … 6=土
  return addDaysIso(iso, -day);
};

/**
 * previousWeek: 基準日の前の日曜〜土曜
 * nextWeek: 基準日を含む日曜〜土曜
 */
export const getWeeklyPeriodRange = (baseIso, kind) => {
  const thisSunday = startOfSundayWeek(baseIso);
  if (kind === 'previous') {
    return {
      periodStart: addDaysIso(thisSunday, -7),
      periodEnd: addDaysIso(thisSunday, -1),
    };
  }
  return {
    periodStart: thisSunday,
    periodEnd: addDaysIso(thisSunday, 6),
  };
};

const listDaysInRange = (periodStart, periodEnd) => {
  const days = [];
  let cursor = periodStart;
  while (cursor <= periodEnd) {
    days.push(cursor);
    cursor = addDaysIso(cursor, 1);
  }
  return days;
};

const resolveHourlyForIso = (calendarData, detailData, locationId, iso) => {
  const day = findCalendarDay(calendarData, iso);
  const { map, source } = resolveHourlyForDate(day, detailData, locationId, iso);
  return { map: map || null, source };
};

/**
 * 禁止表現チェック（テスト用）
 * @param {string} text
 */
export const weeklyHeadlineHasForbiddenPhrase = (text) => {
  if (!text) return false;
  const forbidden = [
    '必ず増えます',
    '混雑します',
    '売上が増えます',
    '来店が増えます',
    '店員を増やしてください',
    '仕込みを増やしてください',
  ];
  return forbidden.some((phrase) => text.includes(phrase));
};

/**
 * 決定的テンプレートで地点 headline を生成（Gemini 未接続）
 * @param {Array<{ weekday: string, statusLabel: string, value: number|null }>} days
 * @param {'actual'|'forecast'} dataKind
 */
/** 3段階「多い」または5段階の多め側（やや多め/多め） */
const isBusyWeeklyDay = (d) => {
  if (!d) return false;
  if (d.fiveLevelCode != null) return Number(d.fiveLevelCode) >= 4;
  if (
    d.statusLabel === WEEKLY_STATUS_LABELS.high
    || d.statusLabel === 'やや多め'
    || d.statusLabel === '多め'
    || d.statusLabel === 'やや多'
    || d.statusLabel === 'やや混んでいる'
    || d.statusLabel === '混んでいる'
  ) {
    return true;
  }
  return false;
};

export const buildWeeklyLocationHeadline = (days, dataKind) => {
  const missing = (days || []).filter((d) => d.value == null).length;
  if (!days?.length || missing >= WEEKLY_INSUFFICIENT_MISSING_THRESHOLD) {
    return 'データを確認できない日があります。';
  }

  const highDays = days.filter(isBusyWeeklyDay);
  const isForecast = dataKind === 'forecast';

  if (!highDays.length) {
    return isForecast
      ? '週を通して大きな変化は少ない見込みです。'
      : '週を通して大きな変化は少ない状態でした。';
  }

  const indices = highDays.map((d) => days.indexOf(d)).sort((a, b) => a - b);
  const ranges = [];
  let start = indices[0];
  let prev = indices[0];
  for (let i = 1; i < indices.length; i += 1) {
    if (indices[i] === prev + 1) {
      prev = indices[i];
    } else {
      ranges.push([start, prev]);
      start = indices[i];
      prev = indices[i];
    }
  }
  ranges.push([start, prev]);

  const longest = ranges.reduce((best, cur) => {
    const len = cur[1] - cur[0];
    const bestLen = best[1] - best[0];
    return len > bestLen ? cur : best;
  }, ranges[0]);

  const startDay = days[longest[0]];
  const endDay = days[longest[1]];

  if (longest[0] === longest[1]) {
    return isForecast
      ? `${startDay.weekday}曜日に人通りが増える見込みです。`
      : `${startDay.weekday}曜日に人通りが多くなりました。`;
  }

  return isForecast
    ? `${startDay.weekday}曜日から${endDay.weekday}曜日にかけて、人通りが増える見込みです。`
    : `${startDay.weekday}曜日と${endDay.weekday}曜日に人通りが多くなりました。`;
};

/**
 * テロップ用の短い地点名（テレビで読み上げやすい）
 */
const shortLocationName = (name = '') => {
  if (name.includes('駅前')) return '駅前';
  if (name.includes('町並み')) return '町並み';
  return name;
};

/** 多い日を「金・土」形式に（連続でも中黒で短く） */
const formatHighWeekdaysCompact = (days = []) => {
  const highs = days.filter(isBusyWeeklyDay);
  if (!highs.length) return null;
  return highs.map((d) => d.weekday).join('・');
};

/**
 * テロップ1本の中心メッセージ（テレビ尺向けに短く）
 * - 2地点が同じ見通し → 1文のまま
 * - 違う → 「駅前は金・土、町並みは日に…」と圧縮（全文の連結はしない）
 */
export const buildWeeklyUnifiedSummary = (locations = [], dataKind) => {
  const valid = (locations || []).filter((loc) => loc?.headline);
  if (!valid.length) return '';

  const kind = dataKind
    || valid[0].dataKind
    || valid[0].days?.[0]?.dataKind
    || 'forecast';
  const isForecast = kind === 'forecast';
  const headlines = valid.map((loc) => loc.headline);

  if (new Set(headlines).size === 1) return headlines[0];

  if (headlines.some((h) => h.includes('データ'))) {
    return isForecast
      ? '地点によって見通しが分かれます。'
      : '地点によって傾向が分かれました。';
  }

  const parts = valid.map((loc) => {
    const daysLabel = formatHighWeekdaysCompact(loc.days);
    const name = shortLocationName(loc.locationName);
    if (!daysLabel) return `${name}は変化少`;
    return `${name}は${daysLabel}`;
  });

  return isForecast
    ? `${parts.join('、')}に増える見込みです。`
    : `${parts.join('、')}に多くなりました。`;
};

/**
 * @returns {import('./tvWeeklyStatus').WeeklyLocationSummary-like}
 */
export const buildWeeklyLocationSummary = ({
  spotId,
  startDate,
  numberOfDays = 7,
  calendarData,
  detailData,
  dataKind,
}) => {
  const spot = getTvSpotConfig(spotId);
  const days = [];

  for (let i = 0; i < numberOfDays; i += 1) {
    const iso = addDaysIso(startDate, i);
    const dt = parseIsoDate(iso);
    const { map } = resolveHourlyForIso(calendarData, detailData, spotId, iso);
    const calibrated = calibrateHourlyMapForTvSpot(spotId, map || {}, { iso });
    const status = hourlyMapToWeeklyDayStatus(
      map && Object.keys(map).length ? calibrated : null,
    );

    days.push({
      date: iso,
      weekday: WEEKDAY_JA[dt.getDay()],
      value: status.value,
      tier: status.tier,
      statusLabel: status.statusLabel,
      dataKind,
    });
  }

  const missingCount = days.filter((d) => d.value == null).length;
  const headline = buildWeeklyLocationHeadline(days, dataKind);

  return {
    spotId,
    locationName: spot.label,
    days,
    headline,
    insufficientData: missingCount >= WEEKLY_INSUFFICIENT_MISSING_THRESHOLD,
  };
};

export const buildWeeklyWeekBlock = ({
  baseIso,
  kind,
  spotResults,
  generatedAtLabel,
}) => {
  const dataKind = kind === 'previous' ? 'actual' : 'forecast';
  const { periodStart, periodEnd } = getWeeklyPeriodRange(baseIso, kind);
  const dayCount = listDaysInRange(periodStart, periodEnd).length;

  const locations = TV_SPOT_LOCATION_OPTIONS.map(({ id }) => {
    const result = spotResults?.[id] || { calendarMerged: [], detail: null };
    return buildWeeklyLocationSummary({
      spotId: id,
      startDate: periodStart,
      numberOfDays: dayCount,
      calendarData: result.calendarMerged,
      detailData: result.detail,
      dataKind,
    });
  });

  const insufficientData = locations.every((loc) => loc.insufficientData);

  return {
    periodStart,
    periodEnd,
    periodLabel: formatWeeklyPeriodLabel(periodStart, periodEnd),
    generatedAtLabel,
    dataKind,
    kindLabel: dataKind === 'actual' ? '計測結果' : 'AIによる予測',
    panelTitle: dataKind === 'actual' ? '先週の人通り' : '今週の人通り予報',
    locations,
    insufficientData,
    summary: buildWeeklyUnifiedSummary(locations, dataKind),
    imageSrc: getTvSpotConfig('station').imageSrc,
  };
};

export const buildWeeklyIntroPanel = ({ nextWeek, previousWeek, periodStart, periodEnd }) => {
  const focus =
    nextWeek?.locations?.find((l) => !l.insufficientData)
    || previousWeek?.locations?.find((l) => !l.insufficientData)
    || nextWeek?.locations?.[0];

  const areaNames = TV_SPOT_LOCATION_OPTIONS.map((o) => o.label).join('・');
  const baseHeadline = focus?.headline
    || '高山市街の人通りの見通しをお伝えします。';

  let headline = baseHeadline;
  if (
    baseHeadline
    && !baseHeadline.startsWith('今週')
    && !baseHeadline.startsWith('データ')
  ) {
    headline = `今週は、${baseHeadline}`;
  }

  return {
    channel: '飛騨高山ケーブルネットワーク 12ch',
    title: '高山の人通り予報',
    periodStart,
    periodEnd,
    periodLabel: formatWeeklyPeriodLabel(periodStart, periodEnd),
    coverageLabel: `対象：${areaNames}`,
    headline,
    trialLabel: '実証中',
  };
};

/** 導入で読み上げた「今週は、」を Web 案内では繰り返さない */
export const stripWeeklyLeadIn = (text) => String(text || '').replace(/^今週は、/, '');

/**
 * Web案内の「目的」リスト
 * チラシ粒度のお悩み表現＋一般住民向け。機能名の列挙はしない。
 * 協議資料「誰に何を届けるか」に合わせ、住民と事業者の双方を出す。
 */
export const WEEKLY_WEB_PURPOSE_GROUPS = [
  {
    audience: 'お出かけの方へ',
    items: [
      '混雑を避けて出かけたい',
      'にぎわいのある時間に街へ行きたい',
    ],
  },
  {
    audience: 'お店の方へ',
    items: [
      '定休日をいつにするか迷っている',
      '仕込みの量がわからない',
    ],
  },
];

export const buildWeeklyWebGuidePanel = ({ intro, generatedAtLabel }) => ({
  headline: stripWeeklyLeadIn(intro?.headline) || '高山市街の人通りの見通し',
  qrUrl: TV_QR_URL,
  description: '気になる日や時間帯は、QRコードから。',
  purposeGroups: WEEKLY_WEB_PURPOSE_GROUPS,
  /** 後方互換：平坦化した目的文 */
  webPurposes: WEEKLY_WEB_PURPOSE_GROUPS.flatMap((g) => g.items),
  disclaimer: 'AIカメラの計測とAIの予測にもとづく参考情報です。',
  generatedAtLabel,
});

/**
 * weekly ペイロード組立
 */
export const assembleWeeklyTvDemoPayload = (raw) => {
  const { targetIso, spotResults, sourceSummary } = raw;
  const generatedAt = new Date();
  const generatedAtLabel = formatGeneratedAtLabel(generatedAt);
  const generatedAtIso = generatedAt.toISOString();

  const previousWeek = buildWeeklyWeekBlock({
    baseIso: targetIso,
    kind: 'previous',
    spotResults,
    generatedAtLabel,
  });
  const nextWeek = buildWeeklyWeekBlock({
    baseIso: targetIso,
    kind: 'next',
    spotResults,
    generatedAtLabel,
  });

  const intro = buildWeeklyIntroPanel({
    nextWeek,
    previousWeek,
    periodStart: nextWeek.periodStart,
    periodEnd: nextWeek.periodEnd,
  });
  const webGuide = buildWeeklyWebGuidePanel({ intro, generatedAtLabel });

  return {
    mode: 'weekly',
    generatedAt: generatedAtIso,
    generatedAtLabel,
    meta: {
      channel: '飛騨高山ケーブルネットワーク 12ch',
      segmentTitle: '高山の人通り予報',
    },
    intro,
    previousWeek,
    nextWeek,
    webGuide,
    fetchedAt: targetIso,
    debug: { sourceSummary, mode: 'weekly' },
  };
};

/** モック用：合成 hourly → spotResults なしで直接日別を作る */
export const buildMockWeeklyDays = (startIso, seedFn, dataKind) => {
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const iso = addDaysIso(startIso, i);
    const dt = parseIsoDate(iso);
    const status = hourlyMapToWeeklyDayStatus(seedFn(i, dt));
    days.push({
      date: iso,
      weekday: WEEKDAY_JA[dt.getDay()],
      value: status.value,
      tier: status.tier,
      statusLabel: status.statusLabel,
      dataKind,
    });
  }
  return days;
};

const TV_WEEKLY_LIVE_LOCATION_ORDER = [
  'station',
  'old-town',
  'jinnya',
  'honmachi3',
  'yasukawadori',
];

const TV_WEEKLY_LIVE_LABELS = {
  station: '駅前',
  'old-town': '古い町並',
  jinnya: '陣屋',
  honmachi3: '本町3丁目',
  yasukawadori: 'やすかわ通り',
};

/**
 * バックエンド /api/tv-demo/weekly-congestion の応答から週間ペイロードを組立
 * （閾値計算はバックエンド側。フロントは表示組立のみ）
 */
export const assembleWeeklyTvDemoPayloadFromApi = (apiData, { profile } = {}) => {
  const generatedAt = new Date();
  const generatedAtLabel = formatGeneratedAtLabel(generatedAt);
  const generatedAtIso = generatedAt.toISOString();

  const mapWeek = (weekBlock, dataKind) => {
    if (!weekBlock) {
      return {
        periodStart: '',
        periodEnd: '',
        periodLabel: '',
        generatedAtLabel,
        dataKind,
        kindLabel: dataKind === 'actual' ? '計測結果' : 'AIによる予測',
        panelTitle: dataKind === 'actual' ? '先週の人通り' : '今週の人通り予報',
        locations: [],
        insufficientData: true,
        summary: '',
        imageSrc: getTvSpotConfig('station').imageSrc,
      };
    }
    const locations = TV_WEEKLY_LIVE_LOCATION_ORDER.map((id) => {
      const daysRaw = weekBlock.locations?.[id] || [];
      const days = daysRaw.map((d) => {
        const dt = parseIsoDate(d.date);
        const missing = d.five_level_code == null || d.daily_mean_level == null;
        return {
          date: d.date,
          weekday: WEEKDAY_JA[dt.getDay()],
          value: missing ? null : d.daily_mean_level,
          tier: missing ? 'none' : `five_${d.five_level_code}`,
          statusLabel: missing ? WEEKLY_STATUS_LABELS.none : d.five_level_label,
          dataKind,
          fiveLevelCode: d.five_level_code,
        };
      });
      const missingCount = days.filter((d) => d.value == null).length;
      return {
        spotId: id,
        locationName: TV_WEEKLY_LIVE_LABELS[id] || id,
        days,
        headline: buildWeeklyLocationHeadline(days, dataKind),
        insufficientData: missingCount >= WEEKLY_INSUFFICIENT_MISSING_THRESHOLD,
      };
    });
    return {
      periodStart: weekBlock.period_start,
      periodEnd: weekBlock.period_end,
      periodLabel: formatWeeklyPeriodLabel(weekBlock.period_start, weekBlock.period_end),
      generatedAtLabel,
      dataKind,
      kindLabel: dataKind === 'actual' ? '計測結果' : 'AIによる予測',
      panelTitle: dataKind === 'actual' ? '先週の人通り' : '今週の人通り予報',
      locations,
      insufficientData: locations.every((loc) => loc.insufficientData),
      summary: buildWeeklyUnifiedSummary(locations, dataKind),
      imageSrc: getTvSpotConfig('station').imageSrc,
    };
  };

  const previousWeek = mapWeek(apiData.previous_week, 'actual');
  const nextWeek = mapWeek(apiData.next_week, 'forecast');
  const intro = buildWeeklyIntroPanel({
    nextWeek,
    previousWeek,
    periodStart: nextWeek.periodStart,
    periodEnd: nextWeek.periodEnd,
  });
  const webGuide = buildWeeklyWebGuidePanel({ intro, generatedAtLabel });

  return {
    mode: 'weekly',
    generatedAt: generatedAtIso,
    generatedAtLabel,
    meta: {
      channel: '飛騨高山ケーブルネットワーク 12ch',
      segmentTitle: '高山の人通り予報',
    },
    intro,
    previousWeek,
    nextWeek,
    webGuide,
    fetchedAt: apiData.base_date,
    debug: {
      mode: 'weekly',
      source: 'live_api',
      profile: profile || apiData.profile,
      profileDescription: apiData.profile_description,
    },
  };
};

export { toIsoDate };
